import { pruneHandoff } from './handoff';
import { landingSource } from '../../shared/visit-source.mjs';
import { FIRST_KEY, SOURCE_FRESH_MS, SOURCE_KEY, visitTags } from '../../shared/out.mjs';

/** What an earlier visit leaves behind: a browser holding one is not new, though it predates FIRST_KEY. */
const EARLIER_VISIT_KEYS = ['tixinfo.affinity.v1', 'tixinfo.theme.v2', 'tixinfo.currency', 'tixinfo.qty.v1'];
/** The day FIRST_KEY is backdated to for those browsers (any day before the note existed works). */
const BEFORE_NOTES = '2026-01-01';

/**
 * Once per page load, before the app renders (src/main.tsx). Never throws, never blocks.
 *
 *  - clears a hand-off record past its two minutes (src/lib/handoff.ts), so what the privacy
 *    policy says about it stays true;
 *  - notes, in this browser only, the KIND of site that sent it (shared/visit-source.mjs: chatgpt,
 *    google, none …, with the time) and the day of its first visit. A click to a marketplace sends
 *    /go/ only those two coarse facts, and only on the tracked URL (shared/out.mjs visitTags), and an
 *    event view sends /api/stats the first (visitSource below), so the counts can say which kinds of
 *    site send buyers and whether they come back — with no cookie and no identifier
 *    (src/pages/Legal.tsx, "Counting without identifying you").
 *
 * localStorage, not sessionStorage: the /out/ page opens in a new tab, which starts with an empty
 * sessionStorage. A page load from one of our own pages (a reload, a new tab) keeps a source noted
 * in the last 6 hours. The /out/ page itself notes nothing.
 */
export const startVisit = () => {
  try {
    pruneHandoff();
  } catch {
    /* never block the page */
  }
  try {
    if (window.location.pathname.startsWith('/out/')) return;
    const ls = window.localStorage;
    const now = Date.now();
    const s = landingSource({ utm: new URLSearchParams(window.location.search).get('utm_source'), referer: document.referrer });
    let keep = false;
    if (s === 'internal') {
      try {
        const prev = JSON.parse(ls.getItem(SOURCE_KEY) ?? 'null') as { t?: unknown } | null;
        keep = !!prev && typeof prev.t === 'number' && now - prev.t >= 0 && now - prev.t < SOURCE_FRESH_MS;
      } catch {
        keep = false;
      }
    }
    if (!keep) ls.setItem(SOURCE_KEY, JSON.stringify({ s, t: now }));
    if (!ls.getItem(FIRST_KEY)) {
      ls.setItem(FIRST_KEY, EARLIER_VISIT_KEYS.some((k) => ls.getItem(k) !== null) ? BEFORE_NOTES : new Date(now).toISOString().slice(0, 10));
    }
  } catch {
    /* storage blocked: a click then counts as "unknown" */
  }
};

/**
 * The kind of site that sent this browser, when noted in the last 6 hours — the rule the /out/ page
 * applies to a click (shared/out.mjs visitTags) — else null (none noted, too old, storage blocked).
 * An event view passes it to /api/stats as ?s= (src/pages/EventDetail.tsx), so the event views in
 * pv_stat carry the same source buckets as the clicks in click_src.
 */
export const visitSource = (): string | null => {
  try {
    return visitTags(window.localStorage, Date.now()).s;
  } catch {
    return null;
  }
};
